import { z } from 'zod';
import { PaymentChannelSchema, PRODUCT_CODE_REGEX, PRODUCT_CODE_MESSAGE } from './payment.js';
export const ProductCodeSchema = z.string().regex(PRODUCT_CODE_REGEX, PRODUCT_CODE_MESSAGE);
export const PRODUCT_CATEGORIES = ['wedding', 'studio'];
export const ProductCategorySchema = z.enum(PRODUCT_CATEGORIES);
/** 구매 상품에 허용되는 credit_lot_source 값. PROMOTION/ADMIN_GRANT/REFUND_REVERSAL은 상품이 아니다 */
export const LOT_SOURCES = ['INVITATION_ACTIVATION', 'STUDIO_PURCHASE', 'SNAP_PURCHASE'];
export const LotSourceSchema = z.enum(LOT_SOURCES);
export const CHANNEL_CODE_KINDS = ['SELLER_CODE', 'ITEM_NO', 'LEGACY'];
export const ChannelCodeKindSchema = z.enum(CHANNEL_CODE_KINDS);
const nonNegInt = z.number().int().min(0);
/**
 * 결제 1건의 구성 라인(payments.items jsonb 원소). FIKET-465.
 * 스마트스토어 주문은 기본상품 + 추가구성상품이 한 결제(orderId)에 섞여 들어오므로
 * "어떤 옵션이 몇 개 팔렸는지"를 라인 단위로 보존한다. 사이트(토스) 결제는 기록하지 않아도
 * 소비자가 (type, 1, amount) 1라인으로 간주한다. 라인 amount 합 = payments.amount.
 */
export const PaymentItemSchema = z.object({
    /** 상품 코드(products.code) — 매출 집계 키 */
    productCode: ProductCodeSchema,
    quantity: z.number().int().min(1),
    /** 라인 결제액(수량 포함, 원). 네이버 totalPaymentAmount */
    amount: z.number().int().min(0),
    /** 매핑에 쓰인 네이버 코드 — 단일상품은 sellerProductCode, 추가구성상품은 itemNo */
    channelCode: z.string().max(64).nullable().optional(),
    channelCodeKind: ChannelCodeKindSchema.nullable().optional(),
    /** 네이버 상품명(+옵션명) 원문 — 코드가 바뀌어도 사람이 읽을 수 있게 */
    optionName: z.string().max(200).nullable().optional(),
    /** 네이버 상품주문번호(productOrderId) — 라인 추적용 */
    productOrderId: z.string().max(64).nullable().optional(),
});
/** products 테이블 행(API 응답 형태, 날짜는 ISO 문자열) */
export const ProductSchema = z.object({
    code: ProductCodeSchema,
    nameKo: z.string().min(1).max(100),
    description: z.string().max(300).nullable(),
    category: ProductCategorySchema,
    price: nonNegInt,
    basePrice: nonNegInt,
    discountPercent: z.number().int().min(0).max(100),
    channels: z.array(PaymentChannelSchema).min(1),
    credits: nonNegInt,
    universalSlots: nonNegInt,
    invitationSlots: nonNegInt,
    thankyouSlots: nonNegInt,
    activatesAccount: z.boolean(),
    selfRefundable: z.boolean(),
    lotSource: LotSourceSchema,
    popular: z.boolean(),
    promoBadge: z.string().max(50).nullable(),
    features: z.array(z.string().max(200)),
    sortOrder: z.number().int(),
    smartstoreUrl: z.string().max(500).nullable(),
    enabled: z.boolean(),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
const priceRule = { message: '판매가는 정가 이하여야 합니다', path: ['price'] };
/** 생성 입력. sortOrder/archived/타임스탬프는 서버가 정한다 */
export const ProductInputSchema = z.object({
    code: ProductCodeSchema,
    nameKo: z.string().min(1).max(100),
    description: z.string().max(300).nullable().optional(),
    category: ProductCategorySchema,
    price: nonNegInt,
    basePrice: nonNegInt,
    channels: z.array(PaymentChannelSchema).min(1, '판매 채널을 하나 이상 선택하세요'),
    credits: nonNegInt.default(0),
    universalSlots: nonNegInt.default(0),
    invitationSlots: nonNegInt.default(0),
    thankyouSlots: nonNegInt.default(0),
    activatesAccount: z.boolean().default(false),
    selfRefundable: z.boolean(),
    lotSource: LotSourceSchema,
    popular: z.boolean().default(false),
    promoBadge: z.string().max(50).nullable().optional(),
    features: z.array(z.string().max(200)).default([]),
    smartstoreUrl: z.string().max(500).nullable().optional(),
    enabled: z.boolean().default(true),
}).refine((v) => v.price <= v.basePrice, priceRule);
/** 수정 입력 — code 금지(strict), 전 필드 optional·기본값 없음(zod v4 partial+default 함정 회피) */
export const ProductPatchSchema = z.object({
    nameKo: z.string().min(1).max(100).optional(),
    description: z.string().max(300).nullable().optional(),
    category: ProductCategorySchema.optional(),
    price: nonNegInt.optional(),
    basePrice: nonNegInt.optional(),
    channels: z.array(PaymentChannelSchema).min(1).optional(),
    credits: nonNegInt.optional(),
    universalSlots: nonNegInt.optional(),
    invitationSlots: nonNegInt.optional(),
    thankyouSlots: nonNegInt.optional(),
    activatesAccount: z.boolean().optional(),
    selfRefundable: z.boolean().optional(),
    lotSource: LotSourceSchema.optional(),
    popular: z.boolean().optional(),
    promoBadge: z.string().max(50).nullable().optional(),
    features: z.array(z.string().max(200)).optional(),
    smartstoreUrl: z.string().max(500).nullable().optional(),
    enabled: z.boolean().optional(),
    /** 낙관적 동시성: 서버 updatedAt과 다르면 409 */
    expectedUpdatedAt: z.string().optional(),
}).strict();
export const ProductChannelCodeSchema = z.object({
    id: z.string(),
    channel: PaymentChannelSchema,
    code: z.string().min(1).max(64),
    kind: ChannelCodeKindSchema,
    productCode: ProductCodeSchema,
    label: z.string().min(1).max(100),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ProductChannelCodeInputSchema = z.object({
    channel: PaymentChannelSchema.default('SMARTSTORE'),
    code: z.string().trim().min(1).max(64),
    kind: ChannelCodeKindSchema,
    productCode: ProductCodeSchema,
    label: z.string().trim().min(1).max(100),
});
export const ProductChannelCodePatchSchema = z.object({
    productCode: ProductCodeSchema.optional(),
    label: z.string().trim().min(1).max(100).optional(),
    kind: ChannelCodeKindSchema.optional(),
}).strict();
/** 어드민 목록 원소 */
export const AdminProductListItemSchema = ProductSchema.extend({
    hasHistory: z.boolean(),
    channelCodeCount: z.number().int(),
    paidCount30d: z.number().int(),
});
/** 시딩·폼 기본값: SNAP_ 접두어 → SNAP_PURCHASE, 그 외 studio → STUDIO_PURCHASE, wedding → INVITATION_ACTIVATION */
export function deriveLotSource(code, category) {
    if (code.startsWith('SNAP_'))
        return 'SNAP_PURCHASE';
    return category === 'studio' ? 'STUDIO_PURCHASE' : 'INVITATION_ACTIVATION';
}
export function computeDiscountPercent(basePrice, price) {
    if (basePrice <= 0)
        return 0;
    return Math.max(0, Math.min(100, Math.round((1 - price / basePrice) * 100)));
}
/** 테이블 행 → 기존 소비자가 읽는 ProductDefinition(type=code). null은 undefined로 */
export function toProductDefinition(p) {
    return {
        type: p.code,
        nameKo: p.nameKo,
        credits: p.credits,
        price: p.price,
        basePrice: p.basePrice,
        discountPercent: p.discountPercent,
        channels: p.channels,
        activatesAccount: p.activatesAccount,
        requiresActivation: false,
        selfRefundable: p.selfRefundable,
        category: p.category,
        universalSlots: p.universalSlots,
        invitationSlots: p.invitationSlots,
        thankyouSlots: p.thankyouSlots,
        popular: p.popular,
        promoBadge: p.promoBadge ?? undefined,
        features: p.features,
        description: p.description ?? undefined,
        enabled: p.enabled,
        lotSource: p.lotSource,
    };
}
//# sourceMappingURL=product.js.map